import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, filter, from, isObservable, map, of, switchMap, take, timeout } from 'rxjs';
import { AuthGuard } from 'shared-service/services/auth/guards/auth.guard';
import { AppStorageService } from 'shared-service/app-storage/app.storage';
import { CurrentTenantService } from '../api/platform-management/current-tenant.service';
import { WORKSPACE_QUERY_PARAM } from './workspace-navigation.service';

/** Run only after authentication; resolve the workspace before route permissions, resolvers and data loads. */
export const authenticatedWorkspaceGuard: CanActivateChildFn = (route, state) => {
    const currentTenant = inject(CurrentTenantService);
    const storage = inject(AppStorageService);
    const router = inject(Router);
    const authentication = AuthGuard(route, state);
    const authenticated$ = isObservable(authentication) ? authentication
        : authentication instanceof Promise ? from(authentication) : of(authentication);
    return authenticated$.pipe(switchMap((allowed) => allowed === true
        ? restoreWorkspace(route, state, currentTenant, storage, router)
        : of(allowed)));
};

export function restoreWorkspace(
    route: ActivatedRouteSnapshot, state: RouterStateSnapshot,
    currentTenant: CurrentTenantService, storage: AppStorageService, router: Router,
) {
    const denied = () => router.createUrlTree(['/access-denied'], {
        queryParams: { returnUrl: state.url, reason: 'denied' },
    });
    const values = route.queryParamMap.getAll(WORKSPACE_QUERY_PARAM);
    // The shell guard also runs for query-only navigation on an already active child.
    let leaf = route;
    while (leaf.firstChild) leaf = leaf.firstChild;
    const legacyValues = leaf.pathFromRoot?.some((parent) => parent.data?.['workspaceTenantAlias'] === 'tenantId')
        ? route.queryParamMap.getAll('tenantId') : [];
    const legacy = legacyValues[0];
    if (legacyValues.length > 1 || (legacy !== undefined && (!/^\d+$/.test(legacy)
        || !Number.isSafeInteger(Number(legacy)) || Number(legacy) <= 0
        || (values.length > 0 && Number(values[0]) !== Number(legacy))))) return of(denied());
    const raw = values[0] ?? legacy;
    const requested = Number(raw);
    if (values.length > 1 || (raw !== undefined && (!/^\d+$/.test(raw) || !Number.isSafeInteger(requested)))) {
        return of(denied());
    }
    return currentTenant.membershipTenantId$().pipe(
        take(1),
        switchMap((membership) => {
            if (raw === undefined) return of(true);
            if (membership !== 1) return of(membership > 0 && membership === requested);
            if (requested === 0) {
                currentTenant.selectTenant(null, true);
                return of(true);
            }
            return currentTenant.platformTenants$().pipe(take(1), map((tenants) => {
                const tenant = tenants.find((item) => item.AccountTenantId === requested);
                if (!tenant) return false;
                if (storage.workspaceTenantId !== requested) currentTenant.selectTenant(tenant.TenantId, true);
                return true;
            }));
        }),
        switchMap((allowed) => allowed
            ? currentTenant.accountTenantId$().pipe(
                filter((tenantId) => raw === undefined || tenantId === requested), take(1), map(() => true),
            )
            : of(denied())),
        timeout(10000),
        catchError(() => of(denied())),
    );
}
